/**
 * O CARIMBO DE IDADE DO DADO — obrigatório no topo de toda tela de relatório.
 *
 * "inclui vendas até 27/07 14:32 · Famiglia Lucco sincronizada até 25/07"
 *
 * Regra da casa: **nenhum número sem idade do dado**. Um relatório que diz
 * "vendeu R$ 12.400" sem dizer até quando enxerga mente por omissão: o dono
 * fecha o mês com a loja que parou de sincronizar dia 25 dentro do total e não
 * tem como saber.
 *
 * Os dois selos:
 *  - `parcial` — alguma loja atrasada entrou no total (§23.7).
 *  - `reconstituído` — o total foi SOMADO, não lido (§11.4.2). Nesta entrega
 *    ele aparece SEMPRE em venda: o OPS não grava total de pedido, então não há
 *    o que ler. Dizer isso na cara da tela é o oposto de fingir apuração.
 */
import React from "react";
import * as AlfredoStyles from "../../styles/components/reports/RelatorioComum";
import type { Carimbo, FonteDoRelatorio } from "../../comum/relatorios/tipos";

interface Props {
  carimbo?: Carimbo;
  fonte?: FonteDoRelatorio;
  /**
   * Telas que NÃO leem venda (Caixa, Estoque, Movimentação, Clientes) não
   * devem dizer "sem venda no recorte" — ali não há venda a incluir, e a frase
   * faria parecer que o filtro não achou nada.
   */
  semVenda?: boolean;
}

/**
 * `timeZone: "UTC"` aqui NÃO converte para UTC — impede a conversão.
 *
 * O horário do legado é hora de PAREDE gravada em `timestamp without time
 * zone`; o driver o monta como se fosse UTC. Formatar no fuso do navegador
 * jogaria a venda das 18:42 para 15:42. Ver o comentário em
 * `comum/relatorios/colunas.ts`.
 */
function formatarMomento(iso: string | null): string {
  if (!iso) return "—";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "—";
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

function formatarDia(iso: string | null): string {
  if (!iso) return "—";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "—";
  return data.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "UTC",
  });
}

const CarimboDoDado: React.FC<Props> = ({ carimbo, fonte, semVenda }) => {
  if (!carimbo) return null;

  return (
    <AlfredoStyles.Bloco>
      <AlfredoStyles.Icone>schedule</AlfredoStyles.Icone>

      <AlfredoStyles.Texto>
        {carimbo.incluiVendasAte ? (
          <>
            Inclui vendas até{" "}
            <AlfredoStyles.Forte>
              {formatarMomento(carimbo.incluiVendasAte)}
            </AlfredoStyles.Forte>
          </>
        ) : semVenda ? (
          "Esta tela não lê venda — a idade abaixo é a do momento da consulta"
        ) : (
          "Sem venda no recorte selecionado"
        )}
        {!semVenda && carimbo.lojas.length > 0 && (
          <>
            {" · "}
            {carimbo.lojas.map((loja, indice) => (
              <React.Fragment key={loja.subsidiaryUuid}>
                {indice > 0 && " · "}
                {loja.nome}
                {loja.sincronizadaAte
                  ? ` sincronizada até ${formatarDia(loja.sincronizadaAte)}`
                  : " sem sincronização conhecida"}
                {loja.atrasada && " (atrasada)"}
              </React.Fragment>
            ))}
          </>
        )}
      </AlfredoStyles.Texto>

      {carimbo.parcial && (
        <AlfredoStyles.Selo
          tom="erro"
          title="Uma loja atrasada entrou neste total: o número da rede está incompleto (§23.7)."
        >
          parcial
        </AlfredoStyles.Selo>
      )}

      {carimbo.reconstituido && (
        <AlfredoStyles.Selo
          tom="atencao"
          title="O total não foi lido de um campo gravado — foi somado a partir das linhas. A origem não grava total de pedido."
        >
          reconstituído
        </AlfredoStyles.Selo>
      )}

      {fonte && (
        <AlfredoStyles.Selo tom="info" title={fonte.observacao}>
          origem: {fonte.schema}
        </AlfredoStyles.Selo>
      )}

      <AlfredoStyles.Rodape>
        Gerado em {formatarMomento(carimbo.geradoEm)} · horários no fuso{" "}
        {carimbo.fuso}
        {fonte?.chaveDeColapso && (
          <>
            {" · "}quantidade colapsada por ({fonte.chaveDeColapso.join(", ")})
          </>
        )}
      </AlfredoStyles.Rodape>
    </AlfredoStyles.Bloco>
  );
};

export default CarimboDoDado;
